"use client";

import { motion } from "framer-motion";

interface ConfidenceIndicatorProps {
  score: number;
  label: string;
  primaryColor: string;
  showPercentage?: boolean;
}

/**
 * Confidence Indicator Component
 * 
 * Displays confidence scores with visual bars and levels
 */
export default function ConfidenceIndicator({
  score,
  label,
  primaryColor,
  showPercentage = true,
}: ConfidenceIndicatorProps) {
  const percentage = Math.round(score * 100);
  const level = score > 0.8 ? 'High' : score > 0.6 ? 'Medium' : 'Moderate';
  const levelColor = score > 0.8 ? primaryColor : score > 0.6 ? `${primaryColor}CC` : `${primaryColor}99`;
  
  return (
    <div className="space-y-1.5">
      <div className="flex items-center justify-between text-xs">
        <span className="opacity-80" style={{ color: primaryColor }}>{label}</span>
        {showPercentage && (
          <span className="font-semibold" style={{ color: primaryColor }}>
            {percentage}% <span className="opacity-70">({level})</span>
          </span>
        )}
      </div>
      <div className="h-2 rounded-full overflow-hidden" style={{
        background: `${primaryColor}15`,
      }}>
        <motion.div
          initial={{ width: 0 }}
          animate={{ width: `${percentage}%` }}
          transition={{ duration: 0.5, ease: "easeOut", delay: 0.1 }}
          className="h-full rounded-full"
          style={{
            background: `linear-gradient(90deg, ${levelColor}, ${primaryColor})`,
            boxShadow: `0 0 8px ${primaryColor}40`,
          }}
        />
      </div>
    </div>
  );
}
